import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { ProductAddNavbarComponent } from './product-add-navbar/product-add-navbar.component'
import { ProductOverviewFormComponent } from './product-overview-form/product-overview-form.component'
import { MatButtonModule } from '@angular/material/button'
import {
  CreateProduct,
  PlasticsDeclarationFormData,
  ProductCloningData,
  ProductForm,
  ProductIngredient,
  ProductOverviewFormData,
} from '../../types/products'
import { ProductIngredientsFormComponent } from './product-ingredients-form/product-ingredients-form.component'
import { IngredientFormData, IngredientFormRow } from '../../types/ingredients'
import { Dialog } from '@angular/cdk/dialog'
import { ActivatedRoute, Router } from '@angular/router'
import { DialogService } from '../../services/dialog.service'
import { ErrorService } from '../../services/error.service'
import { ProductService } from '../../services/product.service'
import { SpinnerService } from '../../services/spinner.service'
import { ApiError } from '../../types/apiError'
import { ROUTES } from '../../constants/routes'
import { TranslationService } from '../../services/translation.service'
import { SubstanceFormData } from '../../types/substances'
import { ProductComponentsFormComponent } from './product-components-form/product-components-form.component'
import { ComponentFormData } from '../../types/components'
import { CommonModule } from '@angular/common'
import {
  RecyclabilityData,
  RecyclabilityFormData,
} from '../../types/recyclability'
import { ProductRecyclabilityFormComponent } from './product-recyclability-form/product-recyclability-form.component'
import { ProductGhsFormComponent } from './product-ghs-form/product-ghs-form.component'
import { firstValueFrom } from 'rxjs'
import { GhsClassificationFormData } from '../../types/ghs'
import { ProductPlasticsDeclarationFormComponent } from './product-plastics-declaration-form/product-plastics-declaration-form.component'
@Component({
  selector: 'app-product-add',
  imports: [
    CommonModule,
    MatButtonModule,
    ProductAddNavbarComponent,
    ProductIngredientsFormComponent,
    ProductComponentsFormComponent,
    ProductOverviewFormComponent,
    ProductRecyclabilityFormComponent,
    ProductGhsFormComponent,
    ProductPlasticsDeclarationFormComponent,
  ],
  templateUrl: './product-add.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-add.component.scss',
})
export class ProductAddComponent implements OnInit {
  private dialog = inject(Dialog)
  private productService = inject(ProductService)
  private spinnerService = inject(SpinnerService)
  private dialogService = inject(DialogService)
  private router = inject(Router)
  private errorService = inject(ErrorService)
  translationService = inject(TranslationService)
  private route = inject(ActivatedRoute)

  currentStep = 0
  lastVisitedStep = 0
  originalProductId: string | null = null
  cloning: boolean = false
  editing: boolean = false

  isPlasticPackaging: boolean | null = null
  productSections = [
    this.translationService.getOverviewText(),
    this.translationService.getRecyclabilityText(),
    this.translationService.getGhsClassificationsText(),
    this.translationService.getIngredientsText(),
    this.translationService.getComponentsText(),
    this.translationService.getPlasticsDeclarationText(),
  ]

  productData: ProductForm = {}
  clonedProductData: ProductForm | null = null

  /**
   * Returns the current component form data, if any.
   * This is used to pass components and groups into the plastics declaration step.
   */
  get componentForm(): ComponentFormData | undefined {
    return this.productData['componentForm'] as ComponentFormData | undefined
  }

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      this.originalProductId = params['originalProductId']
      this.cloning = params['cloning']
      this.editing = params['editing']
      if (this.originalProductId) {
        this.loadProductToCloneOrEdit(this.originalProductId)
      }
    })
  }

  /**
   * Loads and clones a product based on the given product ID.
   * This method fetches product data from the cloning endpoint and maps it to the appropriate form structures.
   * @param {string} productId - The ID of the product to clone.
   */

  loadProductToCloneOrEdit(productId: string) {
    try {
      this.spinnerService.startSpinner()
      this.productService
        .getProductForCloning(productId, this.editing)
        .subscribe({
          next: (cloningData: ProductCloningData) => {
            const {
              product,
              ingredients,
              substances,
              components,
              componentGroups,
              canadianCategories,
              recyclability,
              ghsClassifications,
              plasticsDeclaration,
            } = cloningData

            this.clonedProductData = {
              overviewForm: {
                name: product.name || '',
                isMarketplacePrivate: product.isMarketplacePrivate ?? false,
                allowRequests: product.allowRequests ?? false,
                isCopiedFromSupplier: product.isCopiedFromSupplier ?? false,
                marketplaceCategory: product.marketplaceCategory ?? null,
                marketplaceSubcategory: product.marketplaceSubcategory ?? null,
                requestUnit: product.requestUnit || 'each',
                priceAmount: product.priceAmount ?? null,
                priceCurrency: product.priceCurrency ?? null,
                condition: product.condition ?? null,
                droneFlightTimeMinutes: product.droneFlightTimeMinutes ?? null,
                droneType: product.droneType ?? null,
                droneVideoResolution: product.droneVideoResolution ?? null,
                droneSensorSize: product.droneSensorSize ?? null,
                droneFpv: product.droneFpv ?? null,
                customFields: structuredClone(product.customFields ?? []),
                articleCategory: product.articleCategory || '',
                canadianCategories: canadianCategories || [],
                type: product.type || '',
                primaryArticleIdentifier:
                  product.primaryArticleIdentifier || '',
                otherNames: product.otherNames || '',
                measureUnit: product.measureUnit || '',
                measureValue: product.measureValue || 0,
                gs1Identifier: product.gs1Identifier || '',
                sku: product.sku || '',
                batch: product.batch || '',
                batch_upper: product.batch_upper || '',
                serial: product.serial || '',
                serial_upper: product.serial_upper || '',
                diameter: product.diameter || '',
                width: product.width || '',
                height: product.height || '',
                brand: product.brand || '',
                categoryName: '',
                categoryCode: product.categoryCode || '',
                codeDescription: product.codeDescription || '',
                barcodeIdentifier: product.barcodeIdentifier || '',
                productionInCanada: product.productionInCanada || false,
                description: product.description || '',
                safeUseInstructions: product.safeUseInstructions || '',
                safetyDataSheet: '',
                recyclingInstructions: '',
                disassemblingInstructions:
                  product.disassemblingInstructions || '',
                fullIngredientDisclosure:
                  product.fullIngredientDisclosure || false,
                frenchShortName: product.frenchShortName,
                englishShortName: product.englishShortName,
                picture: product.picture || '',
              },
              ingredientForm: {
                ingredients: this.mapIngredientsToFormRows(ingredients),
                fullIngredientDisclosure: product.fullIngredientDisclosure,
                formName: 'ingredientForm',
              },
              substanceForm: {
                substances: substances || [],
                formName: 'substanceForm',
              },
              componentForm: {
                components: components || [],
                componentGroups: componentGroups || [],
                formName: 'componentForm',
              },
              recyclabilityForm: {
                formName: 'recyclabilityForm',
                isPlasticPackaging: recyclability?.isPlasticPackaging || false,
                recycledContentPercentage:
                  recyclability?.recycledContentPercentage || 0,
                isPackage: recyclability?.isPackage || false,
                packageType: recyclability?.packageType || '',
                provincialRecyclability:
                  recyclability?.provincialRecyclability || [],
                countryRecyclability: recyclability?.countryRecyclability || [],
                isRecyclable: recyclability?.isRecyclable || false,
              },
              ghsClassificationForm: {
                formName: 'ghsClassificationForm',
                ghsClassifications: ghsClassifications,
              },
              plasticsDeclarationForm: {
                napcsCodeId: plasticsDeclaration?.napcsCodeId ?? '',
                productWeightKg: plasticsDeclaration?.productWeightKg ?? 0,
                sourceType: plasticsDeclaration?.sourceType ?? '',
                isResidentialWasteStream:
                  plasticsDeclaration?.isResidentialWasteStream ?? false,
                isIciWasteStream:
                  plasticsDeclaration?.isIciWasteStream ?? false,
                isCdrWasteStream:
                  plasticsDeclaration?.isCdrWasteStream ?? false,
                supplier: plasticsDeclaration?.supplier ?? '',
                category: plasticsDeclaration?.category ?? '',
                subcategory: plasticsDeclaration?.subcategory ?? '',
                productMaterialType:
                  plasticsDeclaration?.productMaterialType ?? '',
                reportingDate: plasticsDeclaration?.reportingDate ?? '',
                plasticResins: plasticsDeclaration?.plasticResins ?? [],
                calculationMethod: plasticsDeclaration?.calculationMethod ?? '',
                calculationMethodOther:
                  plasticsDeclaration?.calculationMethodOther ?? null,
                tags: plasticsDeclaration?.tags ?? [],
              },
            }
            this.isPlasticPackaging =
              this.clonedProductData.recyclabilityForm?.isPlasticPackaging ??
              null
            this.spinnerService.stopSpinner()
          },
          error: (err: ApiError) => {
            this.spinnerService.stopSpinner()
            this.errorService.handleErr(this.dialogService, err, this.dialog)
            if (this.editing)
              void this.router.navigate(['/admin/companies/products'])
          },
        })
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Maps an array of ProductIngredient ingredients to an array of IngredientFormRow.
   * @param {ProductIngredient[]} ingredients - The array of ingredients to map.
   * @returns {IngredientFormRow[]} An array of mapped ingredient form rows.
   */
  private mapIngredientsToFormRows(
    ingredients: ProductIngredient[],
  ): IngredientFormRow[] {
    return ingredients.map((productIngredient) => ({
      id: productIngredient.ingredient!.id,
      name: productIngredient.ingredient!.name,
      nameWithAliases: productIngredient.ingredient!.name,
      mayContain: productIngredient.mayContain,
      doesNotContain: productIngredient.doesNotContain,
      upperConcentration: productIngredient.upperConcentration || 0,
      lowerConcentration: productIngredient.lowerConcentration || 0,
      optionallyDisclosed: productIngredient.optionallyDisclosed || false,
      disclosureRoleIds: productIngredient.disclosureRoleIds,
    }))
  }

  updateStep(index: number) {
    this.currentStep = index
  }

  updateLastVisited(index: number) {
    this.lastVisitedStep = index
  }

  onIsPlasticPackaging(val: boolean) {
    this.isPlasticPackaging = val
  }

  goToNextStep() {
    if (this.currentStep < this.productSections.length - 1) {
      const nextStep = this.currentStep + 1
      this.updateStep(nextStep)

      // note: only update last visited step if its not been visited before
      if (nextStep > this.lastVisitedStep) {
        this.lastVisitedStep = nextStep
      }
    } else {
      this.updateStep(0)
    }
  }

  goToPrevStep() {
    if (this.currentStep !== 0) {
      const prevStep = this.currentStep - 1
      this.updateStep(prevStep)
    } else {
      this.updateStep(0)
    }
  }

  /**
   * Saves the product overview form data and moves to the next step.
   * @param {ProductOverviewFormData} formData - The product overview form data to be saved.
   */
  saveProductFormData(formData: ProductOverviewFormData) {
    this.productData['overviewForm'] = formData
    this.goToNextStep()
  }

  /**
   * Saves the ingredient form data and moves to the next step.
   * @param {IngredientFormData} formData - The ingredient form data to be saved.
   * @returns {Promise<void>} A promise that resolves when the form data is saved.
   */
  saveIngredientFormData(formData: IngredientFormData) {
    this.productData['ingredientForm'] = formData
    this.goToNextStep()
  }

  /**
   * Saves the substance form data and moves to the next step.
   * @param {SubstanceFormData} formData - The substance form data to be saved.
   * @returns {Promise<void>} A promise that resolves when the form data is saved.
   */
  saveSubstanceFormData(formData: SubstanceFormData) {
    this.productData['substanceForm'] = formData
    this.goToNextStep()
  }

  /**
   * Saves the component form data and submits the entire form.
   * @param {ComponentFormDataFormData} formData - The component form data to be saved.
   * @returns {Promise<void>} A promise that resolves when the form data is submitted.
   */
  async saveComponentFormData(formData: ComponentFormData): Promise<void> {
    this.productData['componentForm'] = formData
    this.goToNextStep()
  }

  /**
   * Saves the plastics declaration form data and submits the entire form.
   * @param {PlasticsDeclarationFormData} formData - The plastic declaration form data to be saved.
   * @returns {Promise<void>} A promise that resolves when the form data is submitted.
   */
  async savePlasticsDeclarationFormData(
    formData: PlasticsDeclarationFormData,
  ): Promise<void> {
    this.productData['plasticsDeclarationForm'] = formData
    await this.submitFormData()
  }

  /**
   * Submits the complete form data to create a new product.
   * @returns {Promise<void>} A promise that resolves when the product is successfully created.
   * @throws {ApiError} If there's an error during the product creation process.
   */
  async submitFormData(): Promise<void> {
    try {
      const formInvalid =
        this.productData['overviewForm'] == null &&
        this.productData['recyclabilityForm'] == null

      if (formInvalid) {
        return
      }

      const ingredients = this.productData['ingredientForm']?.ingredients ?? []
      const fullIngredientDisclosure =
        this.productData['ingredientForm']?.fullIngredientDisclosure === 'true'
      const substances = this.productData['substanceForm']?.substances ?? []
      const componentForm = this.productData['componentForm'] as
        ComponentFormData | undefined
      const components = componentForm?.components ?? []
      const componentGroups = componentForm?.componentGroups ?? []
      const ghsClassifications =
        this.productData['ghsClassificationForm']?.ghsClassifications ?? []

      const recyclability = this.productData[
        'recyclabilityForm'
      ] as RecyclabilityData

      const plasticsDeclaration = this.productData[
        'plasticsDeclarationForm'
      ] as PlasticsDeclarationFormData
      const data: CreateProduct = {
        ...this.productData!['overviewForm'],
        ingredients,
        substances,
        components,
        componentGroups,
        ghsClassifications,
        fullIngredientDisclosure,
        recyclability,
        plasticsDeclaration,
      } as CreateProduct

      this.spinnerService.startSpinner()
      if (this.editing && this.originalProductId) {
        await firstValueFrom(
          this.productService.edit(data, this.originalProductId),
        )
      } else {
        await firstValueFrom(this.productService.create(data))
      }

      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getProductsText(),
        this.editing
          ? this.translationService.getProductUpdatedSuccessfullyText()
          : this.translationService.getProductCreatedSuccessfullyText(),
      )
      this.spinnerService.stopSpinner()
      this.router.navigate([
        `/${ROUTES.ADMIN.path}/${ROUTES.COMPANY.path}/${ROUTES.COMPANY_PRODUCTS.path}`,
      ])
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Saves the recyclability form data and moves to the next step.
   * @param {RecyclabilityFormData} formData - The recyclability form data to be saved.
   * @returns {void} A promise that resolves when the form data is saved.
   */
  saveRecyclabilityFormData(formData: RecyclabilityFormData): void {
    this.productData['recyclabilityForm'] = formData
    this.goToNextStep()
  }

  saveGhsClassicationFormData(formData: GhsClassificationFormData): void {
    this.productData['ghsClassificationForm'] = formData
    this.goToNextStep()
  }
}
